import { NumberInput, Select, SimpleGrid, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import FormInputGroup from 'components/forms/FormInputGroup';
import FormWrapper from 'components/forms/FormWrapper';
import HrTermVariantCheckboxes from 'components/forms/hr/HrTermVariantCheckboxes';
import { DateInput } from 'components/inputs/DateInput';
import {
  HrPersonMultiSelect,
  HrPersonSelectOption,
  HrTermTypeSelect,
  HrTermTypeSelectOption,
} from 'components/selects/hr/HrSelects';
import panic from 'errors/panic';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HR_TERMS_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

interface BulkTermFormValues {
  personIds: string[];
  termTypeId: string;
  nextDueDate: string;
  recurrenceQuantity: number;
  recurrenceUnit: string;
  note: string;
  checkedVariantIds: number[];
}

const initialValues: BulkTermFormValues = {
  personIds: [],
  termTypeId: '',
  nextDueDate: '',
  recurrenceQuantity: 0,
  recurrenceUnit: 'none',
  note: '',
  checkedVariantIds: [],
};

/** Dedicated atomic form for assigning one HR term definition to multiple people. */
export default function HrBulkCreateTermsForm() {
  const { getAction } = useApi();
  const navigate = useNavigate();
  const { isTablet } = useResponsiveDesign();
  const [people, setPeople] = useState<HrPersonSelectOption[]>([]);
  const [termTypes, setTermTypes] = useState<HrTermTypeSelectOption[]>([]);
  const [saving, setSaving] = useState(false);
  const form = useForm<BulkTermFormValues>({
    initialValues,
    validate: {
      personIds: (value) => (value.length > 0 ? null : 'Vyberte aspoň jednu osobu'),
      termTypeId: (value) => (value ? null : 'Vyberte typ termínu'),
      nextDueDate: (value) => (value ? null : 'Vyberte najbližší termín'),
      recurrenceQuantity: (value, values) =>
        values.recurrenceUnit !== 'none' && value < 1 ? 'Zadajte opakovanie' : null,
    },
  });
  useDirtyFormHandler(form, true);

  const selectedOrganizationId = useMemo(
    () => people.find((person) => form.values.personIds.includes(String(person.value)))?.organizationId,
    [form.values.personIds, people]
  );

  const isTypeCompatible = useCallback(
    (termType: HrTermTypeSelectOption, organizationId?: number) =>
      organizationId !== undefined && (termType.isGlobal || termType.organizationIds.includes(organizationId)),
    []
  );

  const filterPeopleByOrganization = useCallback(
    (options: HrPersonSelectOption[]) =>
      selectedOrganizationId === undefined
        ? options
        : options.filter((person) => person.organizationId === selectedOrganizationId),
    [selectedOrganizationId]
  );

  const filterCompatibleTypes = useCallback(
    (options: HrTermTypeSelectOption[]) =>
      options.filter((termType) => isTypeCompatible(termType, selectedOrganizationId)),
    [isTypeCompatible, selectedOrganizationId]
  );

  /** Applies a term type's recurrence defaults to the common assignment form. */
  const selectTermType = (value: string | null) => {
    form.setFieldValue('termTypeId', value ?? '');
    const selected = termTypes.find((termType) => String(termType.value) === value);
    const certificateType = selected?.kind === 'technician-certificate';
    form.setFieldValue('recurrenceQuantity', selected?.defaultRecurrence?.quantity ?? (certificateType ? 1 : 0));
    form.setFieldValue('recurrenceUnit', selected?.defaultRecurrence?.unit ?? (certificateType ? 'years' : 'none'));
    form.setFieldValue('checkedVariantIds', []);
  };

  /** Restricts the next person selection to one organization and keeps its type scope valid. */
  const selectPeople = (personIds: string[]) => {
    const firstPerson = people.find((person) => String(person.value) === personIds[0]);
    const organizationId = selectedOrganizationId ?? firstPerson?.organizationId;
    const sameOrganizationPersonIds =
      organizationId === undefined
        ? []
        : personIds.filter((personId) =>
            people.some((person) => String(person.value) === personId && person.organizationId === organizationId)
          );
    form.setFieldValue('personIds', sameOrganizationPersonIds);

    if (sameOrganizationPersonIds.length === 0) {
      selectTermType(null);
      return;
    }

    const selectedType = termTypes.find((termType) => String(termType.value) === form.values.termTypeId);
    if (selectedType && !isTypeCompatible(selectedType, organizationId)) {
      selectTermType(null);
    }
  };

  const submit = form.onSubmit(async (values) => {
    setSaving(true);
    try {
      const result = await getAction('HrBulkCreateTerms')({
        payload: {
          personIds: values.personIds.map(Number),
          termTypeId: Number(values.termTypeId),
          nextDueDate: values.nextDueDate,
          recurrence:
            values.recurrenceUnit === 'none'
              ? undefined
              : { quantity: values.recurrenceQuantity, unit: values.recurrenceUnit },
          note: values.note.trim() || undefined,
          checkedVariantIds: values.checkedVariantIds,
        },
      });
      form.resetDirty(values);
      notifications.show({
        title: 'HR termíny boli vytvorené',
        message: `Vytvorené termíny: ${result.count}.`,
        color: SUCCESS_NOTIFICATION_COLOR,
      });
      navigate(HR_TERMS_PAGE_PATH.original);
    } catch (caught) {
      notifications.show({
        title: 'HR termíny sa nepodarilo vytvoriť',
        message: 'Žiadnej z vybraných osôb nebol vytvorený termín. Skontrolujte údaje a skúste to znova.',
        color: 'red',
      });
      panic(caught as Error);
    } finally {
      setSaving(false);
    }
  });

  const selectedType = termTypes.find((termType) => String(termType.value) === form.values.termTypeId);
  const isCertificateType = selectedType?.kind === 'technician-certificate';

  return (
    <DashboardLayout
      title="Hromadné pridanie HR termínov"
      breadcrumbs={[{ title: 'Termíny', link: HR_TERMS_PAGE_PATH.original }, { title: 'Pridať termíny' }]}
    >
      <FormWrapper
        primaryButtonText="Vytvoriť"
        primaryIcon={<IconPlus stroke={1.5} />}
        secondaryButtonText="Zrušiť"
        onSecondaryClick={() => navigate(HR_TERMS_PAGE_PATH.original)}
        skipSecondaryButtonConfirm={!form.isDirty()}
        onSubmit={submit}
        loading={saving}
      >
        <FormInputGroup groupTitle="Termín">
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <HrPersonMultiSelect
              size="lg"
              label="Osoby"
              placeholder="Vyberte osoby"
              withAsterisk
              clearable
              onReady={setPeople}
              filterData={filterPeopleByOrganization}
              {...form.getInputProps('personIds')}
              onChange={selectPeople}
            />
            <HrTermTypeSelect
              size="lg"
              label="Typ termínu"
              placeholder="Vyberte typ termínu"
              withAsterisk
              clearable
              includeArchived={false}
              onReady={setTermTypes}
              filterData={filterCompatibleTypes}
              {...form.getInputProps('termTypeId')}
              disabled={form.values.personIds.length === 0}
              onChange={selectTermType}
            />
          </SimpleGrid>
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <DateInput size="lg" label="Najbližší termín" withAsterisk {...form.getInputProps('nextDueDate')} />
            <Textarea size="lg" label="Poznámka" {...form.getInputProps('note')} />
          </SimpleGrid>
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <NumberInput
              size="lg"
              label="Opakovanie"
              min={1}
              disabled={form.values.recurrenceUnit === 'none'}
              {...form.getInputProps('recurrenceQuantity')}
            />
            <Select
              size="lg"
              label="Jednotka opakovania"
              data={[
                ...(isCertificateType ? [] : [{ value: 'none', label: 'Bez opakovania' }]),
                { value: 'days', label: 'Dni' },
                { value: 'weeks', label: 'Týždne' },
                { value: 'months', label: 'Mesiace' },
                { value: 'years', label: 'Roky' },
              ]}
              {...form.getInputProps('recurrenceUnit')}
              onChange={(value) => {
                const unit = value ?? (isCertificateType ? 'years' : 'none');
                form.setFieldValue('recurrenceUnit', unit);
                if (unit === 'none') {
                  form.setFieldValue('recurrenceQuantity', 0);
                } else if (form.values.recurrenceQuantity < 1) {
                  form.setFieldValue('recurrenceQuantity', 1);
                }
              }}
            />
          </SimpleGrid>
          <HrTermVariantCheckboxes
            variants={selectedType?.variants ?? []}
            checkedVariantIds={form.values.checkedVariantIds}
            onChange={(variantIds) => form.setFieldValue('checkedVariantIds', variantIds)}
          />
        </FormInputGroup>
      </FormWrapper>
    </DashboardLayout>
  );
}
